
import OptimizedImage from '../../../components/base/OptimizedImage';
import { useState } from 'react';

const AboutSection = () => {
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: '全程保險保障',
      description: '每件物品都有保險，搬運過程安心無憂',
      color: 'teal',
      stat: '100%',
      statLabel: '理賠保障'
    },
    {
      icon: 'ri-time-line',
      title: '準時到達服務',
      description: '嚴格遵守預約時間，絕不讓您久等',
      color: 'emerald',
      stat: '99%',
      statLabel: '準時率'
    },
    {
      icon: 'ri-customer-service-line',
      title: '24小時客服',
      description: '全天候服務支援，隨時為您解答疑問',
      color: 'teal',
      stat: '24/7',
      statLabel: '全天服務'
    },
    {
      icon: 'ri-price-tag-line',
      title: '透明公開報價',
      description: '明確報價無隱藏費用，讓您消費安心',
      color: 'emerald',
      stat: '0',
      statLabel: '隱藏費用'
    }
  ];

  const achievements = [
    { number: '5000+', label: '成功搬遷', icon: 'ri-home-heart-line' },
    { number: '10+', label: '年專業經驗', icon: 'ri-calendar-check-line' },
    { number: '98%', label: '客戶滿意度', icon: 'ri-emotion-happy-line' },
    { number: '4.9', label: 'Google 評分', icon: 'ri-star-fill' }
  ];

  return (
    <section id="about" className="py-16 md:py-24 bg-gradient-to-br from-white via-teal-50/30 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 標題區域 */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <i className="ri-award-line text-lg"></i>
            <span>專業搬家團隊</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            為什麼選擇<span className="text-teal-600">建鈞精緻搬家</span>？
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            超過 <strong className="text-teal-600">10 年</strong> 專業經驗，服務 <strong className="text-teal-600">5000+</strong> 位滿意客戶
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* 左側內容 */}
          <div>
            <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 mb-8 border border-gray-100">
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
                <strong className="text-teal-600">建鈞精緻搬家</strong>是您最值得信賴的搬家夥伴。我們深知每一次搬家對客戶的重要性，專業團隊經過嚴格訓練，使用專業設備與包裝材料，確保每一件物品都能安全、完整地到達目的地。
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                從<strong>套房搬家</strong>到<strong>辦公室搬遷</strong>，從<strong>新北搬家</strong>到<strong>桃園搬家</strong>、<strong>新竹搬家</strong>，我們提供全方位的搬家解決方案，是北台灣最專業的<strong>搬家公司推薦</strong>首選。
              </p>
            </div>

            {/* 特色卡片網格 */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`relative bg-white rounded-2xl p-5 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 group overflow-hidden ${
                    hoveredFeature === index ? 'scale-105' : ''
                  }`}
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                >
                  {/* 背景裝飾 */}
                  <div className={`absolute top-0 right-0 w-20 h-20 rounded-full -mr-10 -mt-10 transition-transform duration-300 group-hover:scale-150 ${
                    feature.color === 'teal' ? 'bg-teal-100' : 'bg-emerald-100'
                  }`}></div>
                  
                  <div className="relative z-10">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                      feature.color === 'teal' 
                        ? 'bg-gradient-to-br from-teal-500 to-teal-600' 
                        : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                    }`}>
                      <i className={`${feature.icon} text-white text-xl`}></i>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1 text-sm md:text-base">{feature.title}</h3>
                    <p className="text-gray-500 text-xs md:text-sm leading-relaxed">{feature.description}</p>
                    
                    {/* 統計數據 */}
                    <div className={`mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 ${
                      feature.color === 'teal' ? 'text-teal-600' : 'text-emerald-600'
                    }`}>
                      <span className="text-lg md:text-xl font-bold">{feature.stat}</span>
                      <span className="text-xs text-gray-500">{feature.statLabel}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 右側圖片與統計 */}
          <div className="relative">
            {/* 主圖片 */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <OptimizedImage 
                src="https://public.readdy.ai/ai/img_res/bba055bd7cbb399444fbd7760e48280a.jpg"
                alt="專業搬家團隊"
                className="w-full h-80 md:h-96"
              />
              
              {/* 漸層遮罩 */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
              
              {/* 圖片上的標籤 */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center">
                      <i className="ri-team-line text-white text-xl"></i>
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">專業搬家團隊</div>
                      <div className="text-sm text-gray-500">經驗豐富 · 訓練有素 · 服務至上</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 成就統計卡片 */}
            <div className="absolute -bottom-8 -left-4 -right-4 md:-left-8 md:-right-8">
              <div className="bg-white rounded-2xl shadow-xl p-4 md:p-6 border border-gray-100">
                <div className="grid grid-cols-4 gap-2 md:gap-4">
                  {achievements.map((item, index) => (
                    <div key={index} className="text-center group">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                        <i className={`${item.icon} text-teal-600 text-lg md:text-xl`}></i>
                      </div>
                      <div className="text-xl md:text-2xl font-bold text-gray-900">{item.number}</div>
                      <div className="text-xs text-gray-500">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 裝飾元素 */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-teal-200 rounded-full opacity-50 blur-2xl"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-emerald-200 rounded-full opacity-50 blur-2xl"></div>
          </div>
        </div>

        {/* 底部 CTA */}
        <div className="mt-20 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <a 
              href="https://line.me/ti/p/@285yavdh"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-4 rounded-full font-semibold hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg hover:shadow-green-500/30 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <img 
                src="https://static.readdy.ai/image/9ef47c451bf9aafd7a63eea67af7d95d/c33a3c3f2fa31df7dfad05bfabc09369.webp" 
                alt="LINE" 
                className="w-5 h-5"
              />
              LINE 免費諮詢
            </a>
            <a 
              href="tel:0978900081"
              className="inline-flex items-center gap-2 bg-white border-2 border-teal-500 text-teal-600 px-8 py-4 rounded-full font-semibold hover:bg-teal-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-lg"></i>
              0978-900-081
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
